import React, { useEffect, useState } from "react";
import { Bell, BellOff, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { permissionState, pushSupported, subscribeToPush } from "../../lib/webPush";
import { getFcmToken } from "../../lib/fcm";

function isCapacitorNative() {
  // True only inside the Android APK / iOS IPA. Web Push would
  // double-deliver as "Chrome notifications" inside the WebView, so we
  // skip it entirely on native and rely on @capacitor/push-notifications.
  const cap = typeof window !== "undefined" ? window.Capacitor : null;
  return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
}

/**
 * Inline "Enable notifications" prompt.
 *
 * Renders nothing if the browser doesn't support push, if permission is
 * already granted (we don't pester users who've opted in), or if the user
 * has previously dismissed this prompt (localStorage key `kb_push_${key}`).
 *
 * Designed to slot into existing pages — looks like a banner card, not a
 * blocking modal. Used by:
 *   • OrderConfirmation screen → key="order_status"  (customer flow)
 *   • Admin dashboard          → key="admin_orders"  (tenant owner flow)
 */
export default function EnableNotificationsPrompt({
  storageKey,
  title,
  message,
  ctaLabel = "Enable",
  variant = "card",          // "card" | "banner"
  testIdPrefix = "enable-push",
}) {
  const [supported, setSupported] = useState(false);
  const [perm, setPerm] = useState("default");
  const [dismissed, setDismissed] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    setSupported(pushSupported());
    if (pushSupported()) setPerm(permissionState());
    try {
      if (localStorage.getItem(`kb_push_${storageKey}`) === "dismissed") {
        setDismissed(true);
      }
    } catch { /* sandboxed iframe */ }
  }, [storageKey]);

  if (!supported) return null;
  if (perm === "granted") return null;
  if (perm === "denied") {
    // Show a one-time "blocked" hint but never auto-render after first dismissal.
    if (dismissed) return null;
    return (
      <BlockedHint
        onDismiss={() => { setDismissed(true); persistDismiss(storageKey); }}
        testIdPrefix={testIdPrefix}
      />
    );
  }
  if (dismissed) return null;

  const enable = async () => {
    setWorking(true);
    try {
      if (isCapacitorNative()) {
        // Native app: skip Web Push (would render as Chrome notifications
        // inside the WebView). Use the @capacitor/push-notifications
        // plugin which talks to FCM/APNs directly and renders NATIVE
        // system notifications with the app's launcher icon.
        const token = await getFcmToken();
        if (!token) throw new Error("Native push registration failed");
      } else {
        // Browser: existing pywebpush via VAPID + optional FCM token.
        await subscribeToPush();
        getFcmToken().catch(() => { /* best effort, parallel channel */ });
      }
      toast.success("Notifications enabled");
      setPerm("granted");
    } catch (e) {
      const blocked = (e && e.message && /denied|blocked|permission/i.test(e.message));
      if (blocked) {
        setPerm("denied");
      } else {
        toast.error(e?.message || "Could not enable notifications");
      }
    } finally {
      setWorking(false);
    }
  };

  const dismiss = () => {
    setDismissed(true);
    persistDismiss(storageKey);
  };

  const Wrapper = variant === "banner" ? BannerWrapper : CardWrapper;
  return (
    <Wrapper testIdPrefix={testIdPrefix}>
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="shrink-0 w-9 h-9 bg-[hsl(var(--kb-primary))]/10 text-[hsl(var(--kb-primary))] flex items-center justify-center rounded-full">
          <Bell className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium">{title}</div>
          <div className="text-xs text-[hsl(var(--kb-muted))] mt-0.5 leading-relaxed">{message}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={enable}
          disabled={working}
          data-testid={`${testIdPrefix}-enable`}
          className="kb-btn-primary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5"
        >
          {working && <Loader2 className="w-3 h-3 animate-spin" />}
          {ctaLabel}
        </button>
        <button
          onClick={dismiss}
          data-testid={`${testIdPrefix}-dismiss`}
          className="p-1.5 text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-text))] rounded"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </Wrapper>
  );
}

function persistDismiss(storageKey) {
  try { localStorage.setItem(`kb_push_${storageKey}`, "dismissed"); } catch { /* sandbox */ }
}

function CardWrapper({ children, testIdPrefix }) {
  return (
    <div
      data-testid={`${testIdPrefix}-card`}
      className="border border-[hsl(var(--kb-border))] bg-white p-3 sm:p-4 flex items-center gap-3 sm:gap-4"
    >
      {children}
    </div>
  );
}

function BannerWrapper({ children, testIdPrefix }) {
  return (
    <div
      data-testid={`${testIdPrefix}-banner`}
      className="border border-amber-200 bg-amber-50 p-3 flex items-center gap-3 sm:gap-4 rounded-md"
    >
      {children}
    </div>
  );
}

function BlockedHint({ onDismiss, testIdPrefix }) {
  return (
    <div
      data-testid={`${testIdPrefix}-blocked`}
      className="border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] p-3 flex items-start gap-3 text-xs"
    >
      <BellOff className="w-4 h-4 shrink-0 mt-0.5 text-[hsl(var(--kb-muted))]" />
      <div className="flex-1">
        Notifications are blocked for this site in your browser settings. To re-enable, click the
        padlock icon next to the URL → Site settings → Notifications → Allow.
      </div>
      <button onClick={onDismiss} className="p-1 text-[hsl(var(--kb-muted))]" aria-label="Dismiss">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
